/**
 * Phone -> user identity resolution for the account-deletion flow.
 *
 * Duplicated on purpose. The public /delete-account page runs this in the
 * browser to capture a `userId` at request time; the approveAccountDeletion
 * callable runs the same logic in Cloud Functions
 * (`functions/accountDeletion/plan.js`) to resolve older requests that have no
 * `userId`. identity.test.ts pins the two implementations against each other —
 * if they drift, a request captured against one user could be resolved to
 * another. Change both, or neither.
 *
 * Western digits only: Arabic-Indic and Eastern-Arabic digits are folded to
 * 0-9 before anything else happens.
 */

/** Saudi mobile, local form. */
export const PHONE_LOCAL_PATTERN = /^05\d{8}$/;

const ARABIC_INDIC_DIGITS = '٠١٢٣٤٥٦٧٨٩';
const EASTERN_ARABIC_DIGITS = '۰۱۲۳۴۵۶۷۸۹';

/**
 * Normalise any user-typed phone into the local `05XXXXXXXX` form.
 * Returns '' when the input is not a Saudi mobile number.
 */
export function normalizePhone(raw: unknown): string {
  if (raw === null || raw === undefined) return '';
  let s = String(raw);

  let folded = '';
  for (const ch of s) {
    const ai = ARABIC_INDIC_DIGITS.indexOf(ch);
    if (ai >= 0) { folded += String(ai); continue; }
    const ea = EASTERN_ARABIC_DIGITS.indexOf(ch);
    if (ea >= 0) { folded += String(ea); continue; }
    folded += ch;
  }
  s = folded.replace(/\D/g, '');

  if (s.startsWith('00966')) s = '0' + s.slice(5);
  else if (s.startsWith('966')) s = '0' + s.slice(3);
  else if (/^5\d{8}$/.test(s)) s = '0' + s;

  return PHONE_LOCAL_PATTERN.test(s) ? s : '';
}

/** Every shape the number might have been stored as on a `users` document. */
export function phoneQueryVariants(raw: unknown): string[] {
  const local = normalizePhone(raw);
  if (!local) return [];
  const bare = local.slice(1);
  return Array.from(new Set([local, `+966${bare}`, `966${bare}`, bare]));
}

export type IdentityPick =
  | { ok: true; userId: string }
  | { ok: false; reason: 'no-match' | 'ambiguous-match' };

/**
 * Accept a match only when the phone points at exactly one user. Zero matches
 * and several matches are both refusals — guessing here would delete the wrong
 * person's account.
 */
export function pickUnambiguousUserId(candidateIds: readonly string[]): IdentityPick {
  const ids = Array.from(new Set(
    (candidateIds || []).map((c) => (typeof c === 'string' ? c.trim() : '')).filter(Boolean),
  ));
  if (ids.length === 0) return { ok: false, reason: 'no-match' };
  if (ids.length > 1) return { ok: false, reason: 'ambiguous-match' };
  return { ok: true, userId: ids[0] };
}
